/**
 * @file Component that implements the Login with Shop Default Authorize flow (with no side-effects).
 */

import {defineCustomElement} from '../../../common/init';
import InputListener from '../../../common/InputListener';
import Bugsnag from '../../../common/bugsnag';
import {I18n} from '../../../common/translator/i18n';
import {IFrameEventSource} from '../../../common/MessageEventSource';
import MessageListener from '../../../common/MessageListener';
import {ShopSheetModal} from '../../../common/shop-sheet-modal/shop-sheet-modal';
import {
  PAY_AUTH_DOMAIN,
  PAY_AUTH_DOMAIN_ALT,
  validateStorefrontOrigin,
} from '../../../common/utils/urls';
import {
  getAnalyticsTraceId,
  updateAttribute,
  isValidEmail,
  exchangeLoginCookie,
  debounce,
  copyTemplateToDom,
} from '../../../common/utils';
import WebComponent from '../../../common/WebComponent';
import {PayMessageSender} from '../../../common/MessageSender';
import {LoginWithShopSdkPageName} from '../../../common/analytics';
import {
  LoginButtonVersion as Version,
  LoginButtonMessageEventData as MessageEventData,
  OAuthParams,
  SdkErrorCode,
  ShopActionType,
  ShopUserMatchedEvent,
} from '../../../types';
import {DefaultComponentMonorailTracker} from '../analytics';
import {buildAuthorizeUrl} from '../authorize';
import {
  createLoginButton,
  LoginWithShopButton,
} from '../components/login-with-shop-button';
import {
  ATTRIBUTE_ANCHOR_SELECTOR,
  ATTRIBUTE_AUTO_OPEN,
  ATTRIBUTE_CLIENT_ID,
  ATTRIBUTE_DISABLE_SIGN_UP,
  ATTRIBUTE_EMAIL_VERIFICATION_REQUIRED,
  ATTRIBUTE_HIDE_BUTTON,
  ATTRIBUTE_REDIRECT_URI,
  ATTRIBUTE_STOREFRONT_ORIGIN,
  ATTRIBUTE_VERSION,
  ATTRIBUTE_ANALYTICS_CONTEXT,
  ATTRIBUTE_ANALYTICS_TRACE_ID,
  ATTRIBUTE_RESPONSE_TYPE,
  ATTRIBUTE_RESPONSE_MODE,
  ATTRIBUTE_CODE_CHALLENGE,
  ATTRIBUTE_CODE_CHALLENGE_METHOD,
  ATTRIBUTE_STATE,
  ATTRIBUTE_SCOPE,
  ATTRIBUTE_USE_PAY_ALT_DOMAIN,
  ERRORS,
  LOAD_TIMEOUT_MS,
  EMAIL_LISTENER_DEBOUNCE_MS,
  SHOP_LOGIN_DEFAULT_STYLE,
  ATTRIBUTE_EMAIL,
} from '../../../constants/loginButton';
import {DefaultComponentAnalyticsContext} from '../../../constants/loginDefault';

import {SHOP_ABG_DEMO_HTML} from './constants';

export default class ShopAbgDemo extends WebComponent {
  _rootElement: ShadowRoot;
  #analyticsTraceId = getAnalyticsTraceId();
  #clientId = '';
  #version: Version = '2';
  #storefrontOrigin = window.location.origin;
  #monorailTracker = new DefaultComponentMonorailTracker({
    elementName: 'shop-login-default',
    analyticsTraceId: this.#analyticsTraceId,
  });

  #i18n: I18n | null = null;

  #redirectUri: string | undefined;

  private _anchorSelector = '';
  private _emailVerificationRequired = false;
  private _hideButton = false;
  private _disableSignUp = false;
  private _autoOpen = false;
  private _analyticsContext = DefaultComponentAnalyticsContext.Default;
  private _usePayAltDomain = false;

  private _continueButton: LoginWithShopButton | undefined;
  private _inputListener: InputListener | undefined;

  private _iframe: HTMLIFrameElement | undefined;
  private _iframeListener: MessageListener<MessageEventData> | undefined;
  private _iframeMessenger?: PayMessageSender;
  private _iframeLoadTimeout: ReturnType<typeof setTimeout> | undefined;
  private _iframeLoaded = false;
  private _payLoaded: Promise<{userFound: boolean}> | undefined;
  private _updateEmailAbortController: AbortController | undefined;
  private _debouncedUpdateEmail: (email: string) => void;
  private _responseType: string | undefined;
  private _responseMode: string | undefined;
  private _codeChallenge: string | undefined;
  private _codeChallengeMethod: string | undefined;
  private _state: string | undefined;
  private _scope: string | undefined;
  private _email = '';

  private _authorizeModal: ShopSheetModal | undefined;
  private _authorizeModalOpened = false;
  private _modalIframeContainer: HTMLDivElement | undefined;
  private _modalHeader: HTMLDivElement | undefined;
  private _modalTitle: HTMLDivElement | undefined;
  private _modalDescription: HTMLHeadingElement | undefined;
  private _modalStartFreeTrialButton: HTMLButtonElement | undefined;

  static get observedAttributes(): string[] {
    return [
      ATTRIBUTE_ANCHOR_SELECTOR,
      ATTRIBUTE_CLIENT_ID,
      ATTRIBUTE_VERSION,
      ATTRIBUTE_STOREFRONT_ORIGIN,
      ATTRIBUTE_EMAIL_VERIFICATION_REQUIRED,
      ATTRIBUTE_HIDE_BUTTON,
      ATTRIBUTE_DISABLE_SIGN_UP,
      ATTRIBUTE_AUTO_OPEN,
      ATTRIBUTE_REDIRECT_URI,
      ATTRIBUTE_ANALYTICS_CONTEXT,
      ATTRIBUTE_ANALYTICS_TRACE_ID,
      ATTRIBUTE_RESPONSE_TYPE,
      ATTRIBUTE_RESPONSE_MODE,
      ATTRIBUTE_CODE_CHALLENGE,
      ATTRIBUTE_CODE_CHALLENGE_METHOD,
      ATTRIBUTE_STATE,
      ATTRIBUTE_SCOPE,
      ATTRIBUTE_USE_PAY_ALT_DOMAIN,
      ATTRIBUTE_EMAIL,
    ];
  }

  constructor() {
    super();

    if (!customElements.get('shop-sheet-modal')) {
      customElements.define('shop-sheet-modal', ShopSheetModal);
    }

    this._rootElement = this.attachShadow({mode: 'open'});
    this._debouncedUpdateEmail = debounce((email) => {
      this._updateEmail(email);

      if (isValidEmail(email)) {
        this.#monorailTracker.trackEmailEnteredAction();
      }
    }, EMAIL_LISTENER_DEBOUNCE_MS);
  }

  attributeChangedCallback(
    name: string,
    _oldValue: string,
    newValue: string | null,
  ): void {
    // For boolean attributes only check if the attribute is passed, ignoring its value
    const attributePassed = Boolean(newValue !== null);

    switch (name) {
      case ATTRIBUTE_VERSION:
        this.#version = newValue as Version;
        this._updateSrc();
        break;
      case ATTRIBUTE_CLIENT_ID:
        this.#clientId = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_RESPONSE_TYPE:
        this._responseType = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_RESPONSE_MODE:
        this._responseMode = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_CODE_CHALLENGE:
        this._codeChallenge = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_CODE_CHALLENGE_METHOD:
        this._codeChallengeMethod = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_STATE:
        this._state = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_SCOPE:
        this._scope = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_STOREFRONT_ORIGIN:
        this.#storefrontOrigin = newValue || window.location.origin;
        validateStorefrontOrigin(this.#storefrontOrigin);
        break;
      case ATTRIBUTE_EMAIL_VERIFICATION_REQUIRED:
        this._emailVerificationRequired = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_HIDE_BUTTON:
        this._hideButton = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_USE_PAY_ALT_DOMAIN:
        this._usePayAltDomain = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_DISABLE_SIGN_UP:
        this._disableSignUp = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_AUTO_OPEN:
        this._autoOpen = attributePassed;
        break;
      case ATTRIBUTE_REDIRECT_URI:
        this.#redirectUri = newValue || undefined;
        break;
      case ATTRIBUTE_ANALYTICS_CONTEXT:
        this._analyticsContext =
          (newValue as DefaultComponentAnalyticsContext) ||
          DefaultComponentAnalyticsContext.Default;
        this._updateMonorailTracker();
        this._updateSrc();
        break;
      case ATTRIBUTE_ANALYTICS_TRACE_ID:
        this.#analyticsTraceId = newValue || getAnalyticsTraceId();
        this._updateMonorailTracker();
        this._updateSrc();
        break;
      case ATTRIBUTE_EMAIL:
        this._initEmail(newValue || '');
        break;
      case ATTRIBUTE_ANCHOR_SELECTOR:
        this._anchorSelector = newValue || '';
        this._authorizeModal?.setAttribute(
          ATTRIBUTE_ANCHOR_SELECTOR,
          this._anchorSelector,
        );
        break;
    }
  }

  async connectedCallback(): Promise<void> {
    await this.#initTranslations();
    this._initElements();
    this._initEvents();
  }

  _initElements() {
    this._rootElement.innerHTML = SHOP_LOGIN_DEFAULT_STYLE;
    if (!this._hideButton) {
      this._continueButton = createLoginButton();
      this._rootElement.appendChild(this._continueButton);
    }
    // this._createAuthorizeModal();
  }

  _initEvents() {
    validateStorefrontOrigin(this.#storefrontOrigin);
    this._continueButton?.addEventListener('click', () => {
      this.#monorailTracker.trackLoginDefaultButtonClicked();
      this._openAuthorizeModal();
    });
  }

  _initEmail(email: string) {
    this._email = email;

    if (isValidEmail(this._email)) {
      this._debouncedUpdateEmail(this._email);
    } else {
      // // Send empty email to Pay to trigger a prop change if the same email is re-submitted
      this._debouncedUpdateEmail('');
    }
  }

  async #initTranslations() {
    try {
      // BUILD_LOCALE is used for generating localized bundles.
      // See ./scripts/i18n-dynamic-import-replacer-rollup.mjs for more info.
      // eslint-disable-next-line no-process-env
      const locale = process.env.BUILD_LOCALE || I18n.getDefaultLanguage();
      const dictionary = await import(`../translations/${locale}.json`);
      this.#i18n = new I18n({[locale]: dictionary});
    } catch (error) {
      if (error instanceof Error) {
        Bugsnag.notify(error);
      }
    }
  }

  disconnectedCallback(): void {
    this._iframeListener?.destroy();
    this._inputListener?.destroy();
  }

  async requestShow() {
    this._createAuthorizeModal();
  }

  /**
   * Sends the given email to Pay if provided. Opens the modal regardless of whether the email
   * matched a user or not or if it was provided at all.
   *
   * @param {string} [email] Optional Email address to initialize the modal with.
   */
  async showPayIFrame(email?: string): Promise<void> {
    if (!this._iframeLoaded) {
      Bugsnag.notify(new Error('requestShow called before iframe loaded'));
      await this._payLoaded;
    }

    if (this._authorizeModalOpened) {
      Bugsnag.notify(
        new Error('requestShow called when the modal is not closed'),
      );
      return;
    }

    if (email) {
      this._updateEmail(email);
    }
  }

  /**
   * Listens for changes to an email input field. If the input contains an email
   * that matches a Pay user, the modal is opened. If email-verification-required
   * is set, the modal will only open for email verified Pay users.
   *
   * Ensure disable-sign-up is set when calling this. Otherwise, if an email does
   * not match a Pay user the sign up form will open in the background but the modal
   * will not be shown.
   *
   * @param {object} inputElement Email input element to listen to.
   * @deprecated Use the email attribute instead
   */
  listenToInput(inputElement: HTMLInputElement): void {
    if (this._inputListener) {
      this._inputListener.destroy();
    }

    const handleNewValue = debounce((value: string) => {
      if (isValidEmail(value)) {
        this._updateEmail(value);
        this.#monorailTracker.trackEmailEnteredAction();
      } else {
        // Send empty email to Pay to trigger a prop change if the same email is re-submitted
        this._updateEmail('');
      }
    }, EMAIL_LISTENER_DEBOUNCE_MS);

    handleNewValue(inputElement.value);
    this._inputListener = new InputListener(inputElement, handleNewValue);
  }

  _createAuthorizeModal() {
    if (this._authorizeModal) {
      this._authorizeModal.remove();
    }

    copyTemplateToDom(
      SHOP_ABG_DEMO_HTML,
      'shop-abg-demo-landing',
      this._rootElement,
    );

    this._authorizeModal = this._rootElement.querySelector(
      'shop-sheet-modal',
    )! as ShopSheetModal;

    // this._authorizeModal = document.createElement('shop-sheet-modal') as SheetModal;

    this._authorizeModal.setAttribute(
      ATTRIBUTE_ANCHOR_SELECTOR,
      this._anchorSelector,
    );
    this._authorizeModal.setAttribute(
      ATTRIBUTE_ANALYTICS_TRACE_ID,
      this.#analyticsTraceId,
    );

    this._modalIframeContainer = document.createElement(
      'div',
    ) as HTMLDivElement;
    this._modalIframeContainer?.classList.add('modal-iframe-container');
    this._modalIframeContainer?.classList.add('sda-hidden');

    this._modalStartFreeTrialButton = this._authorizeModal.querySelector(
      'button',
    ) as HTMLButtonElement;

    this._modalStartFreeTrialButton.addEventListener('click', () => {
      // hide interstitial content
      this._showSignInModal();
    });

    this._authorizeModal
      .querySelector('.shop-abg-member-link')
      ?.addEventListener('click', () => {
        // hide interstitial content
        this._showSignInModal();
      });

    const authenticLogo = document.createElement('div');
    authenticLogo.innerHTML = `
      <div class="shop-abg-logo sda-hidden">
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <circle cx="32" cy="32" r="29.5" stroke="#283F93" stroke-width="5"/>
          <rect x="12" y="16" width="39" height="28" fill="url(#pattern50)"/>
          <defs>
            <pattern id="pattern50" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlink:href="#image0_0_50" transform="matrix(0.0184089 0 0 0.025641 -0.00624589 0)"/>
            </pattern>
            <image id="image0_0_50" width="55" height="39" xlink:href="data:image/png;base64,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"/>
          </defs>
        </svg>
      </div>
      `;

    this._authorizeModal.insertBefore(
      authenticLogo,
      this._authorizeModal.firstChild,
    );

    this._modalHeader = document.createElement('div');
    this._modalHeader.classList.add('shop-abg-header');

    this._modalTitle = document.createElement('h2');
    this._modalTitle.classList.add('shop-abg-header-title');
    this._modalTitle.textContent =
      this.#i18n?.translate('abg_demo.auth_modal.title') ?? '';
    this._modalHeader.appendChild(this._modalTitle);

    this._modalDescription = document.createElement('div');
    this._modalDescription.classList.add('shop-abg-header-description');
    this._modalDescription.innerHTML =
      this.#i18n?.translate('abg_demo.auth_modal.description') ?? '';
    this._modalHeader.appendChild(this._modalDescription);

    this._modalIframeContainer.appendChild(this._modalHeader);
    this._modalIframeContainer.appendChild(this.#createIframe());

    this._authorizeModal.appendChild(this._modalIframeContainer);

    const brandsDiv =
      this._authorizeModal.querySelector<HTMLDivElement>('.shop-abg-brands');
    const brandsImageData = window.Shopify.SignInWithShop?.abgBrandsImageData;
    if (brandsDiv && brandsImageData) {
      brandsDiv.style.backgroundImage = `url(${brandsImageData})`;
      brandsDiv.style.height = '140px';
      brandsDiv.style.margin = '24px 12px';
      brandsDiv.style.backgroundSize = 'contain';
      brandsDiv.style.backgroundRepeat = 'no-repeat';
      brandsDiv.style.backgroundPosition = 'center';
    }

    this._authorizeModal.addEventListener(
      'modalcloserequest',
      this._closeAuthorizeModal.bind(this),
    );

    // hide modal Shop logo
    this._authorizeModal.shadowRoot
      ?.querySelector('.sda-shop-logo')
      ?.classList.add('sda-hidden');

    this._rootElement.appendChild(this._authorizeModal);

    this._authorizeModal.open();
  }

  /**
   * If Pay already loaded, open the modal.
   * Otherwise, set modal to "Mounting" and it will be opened as soon as Pay loads.
   */
  async _openAuthorizeModal() {
    await this._payLoaded;

    if (!this._authorizeModalOpened) {
      this._authorizeModalOpened = true;
      this._authorizeModal!.open();
      this.#monorailTracker.trackPageImpression({
        page: LoginWithShopSdkPageName.AuthorizeModal,
      });
    }
  }

  async _closeAuthorizeModal() {
    if (this._authorizeModal) {
      await this._authorizeModal.close();
      this._authorizeModalOpened = false;
    }

    this._continueButton?.setFocused();
  }

  #createIframe(): HTMLIFrameElement {
    this._iframe = document.createElement('iframe');
    this._iframe.tabIndex = 0;
    this._updateSrc();

    updateAttribute(this._iframe, 'allow', 'publickey-credentials-get *');

    const eventDestination: Window | undefined =
      this.ownerDocument?.defaultView || undefined;

    this._iframeListener = new MessageListener<MessageEventData>(
      new IFrameEventSource(this._iframe),
      [PAY_AUTH_DOMAIN, PAY_AUTH_DOMAIN_ALT, this.#storefrontOrigin],
      this.#handlePostMessage.bind(this),
      eventDestination,
    );

    this._iframeMessenger = new PayMessageSender(this._iframe);
    this._payLoaded = this._iframeListener.waitForMessage('loaded');

    // eslint-disable-next-line promise/catch-or-return
    this._payLoaded.then(({userFound}) => {
      this._iframeLoaded = true;
      this.#clearLoadTimeout();

      if (userFound && this._autoOpen) {
        this._updateEmailAbortController?.abort();
        this._openAuthorizeModal();
      }
    });

    return this._iframe;
  }

  async _updateEmail(email: string): Promise<void> {
    /**
     * Ensures that we do not submit a new email to the iframe if the sheet modal is already visible to the user
     */
    if (this._authorizeModalOpened) {
      return;
    }

    if (
      this._updateEmailAbortController &&
      !this._updateEmailAbortController?.signal.aborted
    ) {
      this._updateEmailAbortController.abort();
    }

    this._updateEmailAbortController = new AbortController();

    try {
      const loaded = this._iframeLoaded;
      const {userFound} = await this._payLoaded!;

      if (!loaded && userFound && this._autoOpen) {
        return;
      }

      this._iframeMessenger?.postMessage({
        type: 'emailsubmitted',
        email,
        hideChange: email.length > 0,
      });

      const shopUserMatchedPromise = this._iframeListener!.waitForMessage(
        'shop_user_matched',
        this._updateEmailAbortController.signal,
      );
      const captchaChallengePromise = new Promise((resolve, reject) => {
        const waitForCaptcha = async () => {
          try {
            const {code} = await this._iframeListener!.waitForMessage(
              'error',
              this._updateEmailAbortController!.signal,
            );

            if (code === SdkErrorCode.CaptchaChallenge) {
              resolve(undefined);
            } else {
              waitForCaptcha();
            }
          } catch (err) {
            reject(err);
          }
        };

        waitForCaptcha();
      });

      await Promise.race([shopUserMatchedPromise, captchaChallengePromise]);

      this._openAuthorizeModal();

      // Ensures `waitForMessage` calls are cancelled after the modal is opened
      this._updateEmailAbortController.abort();
    } catch (err) {
      // no-op
    }
  }

  _updateSrc() {
    if (this._iframe) {
      const oauthParams: OAuthParams = {
        clientId: this.#clientId,
        responseType: this._responseType,
        responseMode: this._responseMode,
        redirectUri: this.#redirectUri,
        codeChallenge: this._codeChallenge,
        codeChallengeMethod: this._codeChallengeMethod,
        state: this._state,
        scope: this._scope,
      };
      const authorizeUrl = buildAuthorizeUrl({
        version: this.#version,
        analyticsTraceId: this.#analyticsTraceId,
        analyticsContext: this._analyticsContext,
        flow: ShopActionType.Default,
        emailVerificationRequired: this._emailVerificationRequired,
        signUpEnabled: !this._disableSignUp,
        ...(this.#version === '1' && this.#clientId && {oauthParams}),
        usePayAltDomain: this._usePayAltDomain,
        hideCopy: true,
      });

      if (!authorizeUrl) return;

      this.#initLoadTimeout();
      updateAttribute(this._iframe, 'src', authorizeUrl);
      Bugsnag.leaveBreadcrumb('Iframe url updated', {authorizeUrl}, 'state');
    }
  }

  _updateMonorailTracker() {
    this.#monorailTracker = new DefaultComponentMonorailTracker({
      elementName: 'shop-login-default',
      analyticsTraceId: this.#analyticsTraceId,
      analyticsContext: this._analyticsContext,
    });
  }

  #initLoadTimeout() {
    this.#clearLoadTimeout();
    this._iframeLoadTimeout = setTimeout(() => {
      const error = ERRORS.temporarilyUnavailable;
      this.dispatchCustomEvent('error', {
        message: error.message,
        code: error.code,
      });
      // eslint-disable-next-line no-warning-comments
      // TODO: replace this bugsnag notify with a Observe-able event
      // Bugsnag.notify(
      //   new PayTimeoutError(`Pay failed to load within ${LOAD_TIMEOUT_MS}ms.`),
      //   {component: this.#component, src: this.iframe?.getAttribute('src')},
      // );
      this.#clearLoadTimeout();
    }, LOAD_TIMEOUT_MS);
  }

  #clearLoadTimeout() {
    if (!this._iframeLoadTimeout) return;
    clearTimeout(this._iframeLoadTimeout);
    this._iframeLoadTimeout = undefined;
  }

  _showSignInModal(): void {
    this._modalHeader?.classList.add('shop-abg-header-ulc');
    this._modalTitle?.classList.add('shop-abg-header-title-ulc');

    this._authorizeModal
      ?.querySelector('.shop-abg-header')
      ?.classList.add('sda-hidden');
    this._authorizeModal
      ?.querySelector('.shop-abg-content')
      ?.classList.add('sda-hidden');

    // show Authentic logo
    this._authorizeModal
      ?.querySelector('.shop-abg-logo')
      ?.classList.remove('sda-hidden');

    // show modal Shop logo
    this._authorizeModal?.shadowRoot
      ?.querySelector('.sda-shop-logo')
      ?.classList.remove('sda-hidden');

    // show iframe
    this._modalIframeContainer?.classList.remove('sda-hidden');

    // Add subscription product
    this._addProductTocart();
  }

  async _addProductTocart(): Promise<void> {
    const productId = window.Shopify.SignInWithShop.AbgDemoProductId;
    if (productId) {
      const formData = {
        items: [
          {
            id: productId,
            quantity: 1,
          },
        ],
        sections: 'cart-drawer,cart-icon-bubble',
      };
      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })
        .then(async (response) => {
          const data = await response.json();

          const currentBubble = document.querySelector('#cart-icon-bubble');
          if (currentBubble) {
            currentBubble.innerHTML = data.sections['cart-icon-bubble'];
          }

          const drawerTemp = document.createElement('div');
          drawerTemp.innerHTML = data.sections['cart-drawer'];
          const currentDrawer = document.querySelector('cart-drawer');
          const newDrawer = drawerTemp.querySelector('cart-drawer');
          if (currentDrawer && newDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
            currentDrawer.classList.remove('is-empty');
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(`Error adding product ${productId} to cart:`, error);
        });
    } else {
      // eslint-disable-next-line no-console
      console.warn('No product ID set for ABG demo.');
    }
  }

  _handleResizeIframe(height: number, width: number) {
    this._iframe!.style.height = `${height}px`;
    this._iframe!.style.width = `${width}px`;
  }

  async _handleCompleted(
    loggedIn: boolean,
    email?: string,
    customerAccessToken?: string,
    customerAccessTokenExpiresAt?: string,
    shouldFinalizeLogin?: boolean,
  ) {
    if (loggedIn && shouldFinalizeLogin) {
      await exchangeLoginCookie(this.#storefrontOrigin, Bugsnag.notify);
    }

    this._authorizeModal!.close();
    this._iframeListener?.destroy();
    this._inputListener?.destroy();

    this.dispatchCustomEvent('completed', {
      loggedIn,
      email,
      customerAccessToken,
      customerAccessTokenExpiresAt,
    });

    if (this.#redirectUri) {
      try {
        let redirectUri = this.#redirectUri;
        if (this.#analyticsTraceId) {
          const queryParams = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            analytics_trace_id: this.#analyticsTraceId,
          };
          const searchParams = new URLSearchParams(queryParams);
          redirectUri = redirectUri.concat(`?${searchParams.toString()}`);
        } else {
          Bugsnag.notify(
            new Error(
              'Missing analytics trace ID when redirecting to account page',
            ),
          );
        }

        window.location.assign(redirectUri);
      } catch (error) {
        if (error instanceof Error) {
          Bugsnag.notify(error);
        }
      }
    }
  }

  _handleError(message: string, code: SdkErrorCode): void {
    this.dispatchCustomEvent('error', {
      message,
      code,
    });
    Bugsnag.notify(new Error(`Authorize Error: ${message} (${code}).`));
    this.#clearLoadTimeout();
  }

  _onUserMatched({
    maskedPhoneNumber,
    userCookieExists,
  }: ShopUserMatchedEvent): void {
    this.dispatchCustomEvent('shopusermatched');

    if (!userCookieExists) {
      // User verification started
      if (this._modalTitle) {
        this._modalTitle.textContent =
          this.#i18n?.translate('abg_demo.auth_modal.login_sms_title') ??
          `Confirm it's you`;
      }
      if (this._modalDescription && maskedPhoneNumber) {
        this._modalDescription.innerHTML =
          this.#i18n?.translate('abg_demo.auth_modal.login_sms_description', {
            phoneNumber: maskedPhoneNumber,
          }) ?? `Enter the code sent to ${maskedPhoneNumber}`;

        this._modalDescription?.classList.add(
          'shop-abg-description-divider-visible',
        );
      }
    }
  }

  _onUserNotMatched(): void {
    this.dispatchCustomEvent('shopusernotmatched');
  }

  #handlePostMessage(data: MessageEventData) {
    switch (data.type) {
      case 'resize_iframe':
        this._handleResizeIframe(data.height, data.width);
        break;
      case 'completed':
        this._handleCompleted(
          data.loggedIn,
          data.email,
          data.customerAccessToken,
          data.customerAccessTokenExpiresAt,
          data.shouldFinalizeLogin,
        );
        break;
      case 'error':
        this._handleError(data.message, data.code);
        break;
      case 'close_requested':
        this._closeAuthorizeModal();
        break;
      case 'shop_user_matched':
        this._onUserMatched(data);
        break;
      case 'shop_user_not_matched':
        this._onUserNotMatched();
        break;
    }
  }
}

/**
 * Define the shop-abg-demo custom element.
 */
export function defineElement() {
  defineCustomElement('shop-abg-demo', ShopAbgDemo);
}
